import { api } from '../apiClient';

// 고객사 관리
export const customerAdminAPI = {
  getCustomers: (skip = 0, limit = 100, includeDeleted = false) => {
    return api.get(`/admin/customers?skip=${skip}&limit=${limit}&include_deleted=${includeDeleted}`).then(response => response.data);
  },
  
  getCustomer: (customerId: number) => {
    return api.get(`/admin/customers/${customerId}`).then(response => response.data);
  },
  
  createCustomer: (customerData: any) => {
    return api.post('/admin/customers', customerData).then(response => response.data);
  },
  
  updateCustomer: (customerId: number, customerData: any) => {
    return api.patch(`/admin/customers/${customerId}`, customerData).then(response => response.data);
  },
  
  deactivateCustomer: (customerId: number) => {
    return api.patch(`/admin/customers/${customerId}/deactivate`).then(response => response.data);
  },
  
  activateCustomer: (customerId: number) => {
    return api.patch(`/admin/customers/${customerId}/activate`).then(response => response.data);
  },
  
  deleteCustomer: (customerId: number) => {
    return api.delete(`/admin/customers/${customerId}`);
  },
  
  getCustomerUsers: (customerId: number, skip = 0, limit = 100, statusFilter?: string, searchQuery?: string, includeDeleted = false, roleFilter?: string) => {
    let url = `/admin/users?customer_id=${customerId}&skip=${skip}&limit=${limit}&include_deleted=${includeDeleted}`;
    if (statusFilter) {
      url += `&status_filter=${encodeURIComponent(statusFilter)}`;
    }
    if (searchQuery) {
      url += `&search_query=${encodeURIComponent(searchQuery)}`;
    }
    if (roleFilter) {
      url += `&role_filter=${encodeURIComponent(roleFilter)}`;
    }
    return api.get(url).then(response => response.data);
  },
  
  getCustomerServices: (customerId: number) => {
    return api.get(`/admin/customers/${customerId}/services`).then(response => response.data);
  },
  
  connectServiceToCustomer: (customerId: number, serviceSlug: string) => {
    return api.post(`/admin/customers/${customerId}/services/${serviceSlug}`).then(response => response.data);
  },

  disconnectServiceFromCustomer: (customerId: number, serviceSlug: string) => {
    return api.delete(`/admin/customers/${customerId}/services/${serviceSlug}`).then(response => response.data);
  },
  
  getActiveCustomers: (skip = 0, limit = 50) => {
    return api.get(`/admin/active-customers?skip=${skip}&limit=${limit}`).then(response => response.data);
  },
  
  getCustomerDashboard: (customerId: number) => {
    return api.get(`/admin/customers/${customerId}/dashboard`).then(response => response.data);
  },
  
  // 가입 차단 목록 (도메인 불허/차단 로그)
  getBlockedSignups: (customerId: number | null, days = 28, includeDeleted = false) => {
    if (customerId === null) {
      // 슈퍼관리자용: 모든 고객사의 차단목록 조회
      return api
        .get(`/admin/blocked-signups/all`, { params: { days, include_deleted: includeDeleted } })
        .then(response => response.data);
    }
    return api
      .get(`/admin/customers/${customerId}/blocked-signups`, { params: { days, include_deleted: includeDeleted } })
      .then(response => response.data);
  },
  
  clearBlockedSignups: (customerId: number) => {
    return api
      .delete(`/admin/customers/${customerId}/blocked-signups`)
      .then(response => response.data);
  },
  
  // 개별 차단해제
  unblockSignup: (logId: number) => {
    return api
      .post(`/admin/blocked-signups/${logId}/unblock`)
      .then(response => response.data);
  },
  
  // 모두 차단해제 (28일 이내)
  unblockAllRecentSignups: (customerId: number | null, days = 28) => {
    if (customerId === null) {
      // 슈퍼관리자용: 미할당 차단목록 해제 (customer_id=null)
      return api
        .post(`/admin/blocked-signups/all/unblock-all`, null, { params: { days, customer_id: null } })
        .then(response => response.data);
    }
    return api
      .post(`/admin/customers/${customerId}/blocked-signups/unblock-all`, null, { params: { days } })
      .then(response => response.data);
  },
  
  // 특정 고객사의 모두 차단해제 (28일 이내)
  unblockAllRecentSignupsForCustomer: (customerId: number, days = 28) => {
    return api
      .post(`/admin/customers/${customerId}/blocked-signups/unblock-all`, null, { params: { days } })
      .then(response => response.data);
  },
  
  // 고객 관리자가 자신의 고객 정보 수정
  updateMyCustomer: (data: { 
    customer_name?: string; 
    alias?: string; 
    auto_approval?: boolean;
    auto_approval_domains?: string;
    contact_name?: string;
    contact_email?: string;
    contact_phone?: string;
  }) => {
    return api.patch('/admin/my-customer', data).then(response => response.data);
  },
};

// 사용자 관리
export const userAdminAPI = {
  getAllUsers: (skip = 0, limit = 50, customerId?: number, statusFilter?: string, searchQuery?: string, includeDeleted = false, roleFilter?: string) => {
    let url = `/admin/all-users?skip=${skip}&limit=${limit}&include_deleted=${includeDeleted}`;
    if (customerId !== undefined) {
      url += `&customer_id=${customerId}`;
    }
    if (statusFilter) {
      url += `&status_filter=${statusFilter}`;
    }
    if (searchQuery) {
      url += `&search_query=${encodeURIComponent(searchQuery)}`;
    }
    if (roleFilter) {
      url += `&role_filter=${roleFilter}`;
    }
    return api.get(url).then(response => response.data);
  },
  
  inviteUser: (email: string, customerId: number, role = 'user', permission?: 'readwrite' | 'readonly') => {
    return api.post('/admin/users/invite', { email, customer_id: customerId, role, permission });
  },
  
  inviteUsersBulk: (
    emails: string[],
    customerId: number,
    role = 'user',
    permission?: 'readwrite' | 'readonly',
    authType?: 'password' | 'oauth',
    passwordType?: 'manual' | 'random',
    manualPassword?: string,
    sendEmail: boolean = true,
  ) => {
    return api.post('/admin/users/invite-bulk', { 
      emails, 
      customer_id: customerId, 
      role, 
      permission,
      auth_type: authType,
      password_type: passwordType,
      manual_password: manualPassword,
      send_email: sendEmail,
    });
  },
  
  approveUser: (userId: number, customerId?: number, customerName?: string) => {
    const body: any = {};
    if (customerId) {
      body.customer_id = customerId;
    }
    if (customerName) {
      body.customer_name = customerName;
    }
    return api.patch(`/users/${userId}/approve`, Object.keys(body).length > 0 ? body : undefined);
  },
  
  deactivateUser: (userId: number) => {
    return api.patch(`/users/${userId}/deactivate`);
  },
  
  updateUser: (userId: number, userData: { name?: string; role?: string; permission?: string; status?: string }) => {
    return api.patch(`/admin/users/${userId}`, userData).then(response => response.data);
  },
  
  deleteUser: (userId: number) => {
    return api.delete(`/admin/users/${userId}`).then(response => response.data);
  },
  
  // INVITED 사용자 임시 비밀번호 설정 + 초대 메일 재발송
  updateAndResendInvitation: (userId: number, mode: 'manual' | 'random', manualPassword?: string) => {
    return api
      .post(`/admin/users/${userId}/invitation/update-and-resend`, {
        mode,
        manual_password: manualPassword,
      })
      .then(response => response.data);
  },

  // 관리자가 사용자 비밀번호 재설정 (ID/PW 사용자만)
  adminResetPassword: (userId: number, newPassword: string) => {
    return api.post(`/admin/users/${userId}/reset-password`, { new_password: newPassword }).then(response => response.data);
  },
  
  // 관리자가 사용자 로그인 유형 변경
  adminChangeAuthType: (userId: number, authType: 'password' | 'oauth' | 'pending_oauth', newPassword?: string) => {
    const payload: { auth_type: string; new_password?: string } = { auth_type: authType };
    if (newPassword) {
      payload.new_password = newPassword;
    }
    return api.post(`/admin/users/${userId}/change-auth-type`, payload).then(response => response.data);
  },
  
  // 이용자 관리 API
  getPendingUsers: (skip = 0, limit = 50, customerId?: number | null, statusFilter?: string, searchQuery?: string) => {
    let url = `/admin/pending-users?skip=${skip}&limit=${limit}`;
    if (customerId !== undefined && customerId !== null) {
      url += `&customer_id=${customerId}`;
    } else if (customerId === null) {
      // null은 미입력을 의미하므로 0으로 전달
      url += `&customer_id=0`;
    }
    if (statusFilter) {
      url += `&status_filter=${encodeURIComponent(statusFilter)}`;
    }
    if (searchQuery) {
      url += `&search_query=${encodeURIComponent(searchQuery)}`;
    }
    return api.get(url).then(response => response.data);
  },

  // 이용자 관리 탭 카운트
  getUserTabCounts: () => {
    return api.get('/admin/user-tab-counts').then(response => response.data as {
      all_users: number;
      pending: number;
      blocked: number;
    });
  },
};

// 서비스 관리
export const serviceAdminAPI = {
  getServiceCounts: () => {
    return api.get('/admin/services/counts', { params: { _t: Date.now().toString() } }).then(response => response.data);
  },
  
  getServices: (statusFilter?: string) => {
    const params: Record<string, string> = { _t: Date.now().toString() }; // 캐시 버스터
    if (statusFilter) {
      params.status_filter = statusFilter;
    }
    return api.get('/admin/services', { params }).then(response => response.data);
  },
  
  getService: (serviceSlug: string) => {
    return api.get(`/admin/services/${serviceSlug}`).then(response => response.data);
  },

  approveService: (serviceSlug: string, isPublic: boolean = false) => {
    return api.post(`/admin/services/${serviceSlug}/approve`, null, {
      params: { is_public: isPublic }
    }).then(response => response.data);
  },

  bulkServiceAction: (serviceSlugs: string[], action: string, isPublic: boolean = false) => {
    return api.post('/admin/services/bulk-action', serviceSlugs, {
      params: { action, is_public: isPublic }
    }).then(response => response.data);
  },

  rejectService: (serviceSlug: string) => {
    return api.post(`/admin/services/${serviceSlug}/reject`).then(response => response.data);
  },

  suspendService: (serviceSlug: string) => {
    return api.post(`/admin/services/${serviceSlug}/suspend`).then(response => response.data);
  },

  resumeService: (serviceSlug: string) => {
    return api.post(`/admin/services/${serviceSlug}/resume`).then(response => response.data);
  },

  toggleServiceVisibility: (serviceSlug: string, isPublic: boolean) => {
    return api.patch(`/services/${serviceSlug}/visibility?is_public=${isPublic}`).then(response => response.data);
  },

  deleteService: (serviceSlug: string) => {
    return api.delete(`/admin/services/${serviceSlug}`).then(response => response.data);
  },

  restoreService: (serviceSlug: string) => {
    return api.post(`/admin/services/${serviceSlug}/restore`).then(response => response.data);
  },

  cancelRejection: (serviceSlug: string) => {
    return api.post(`/admin/services/${serviceSlug}/cancel-rejection`).then(response => response.data);
  },

  permanentlyDeleteService: (serviceSlug: string) => {
    return api.delete(`/admin/services/${serviceSlug}/permanent`).then(response => response.data);
  },
  
  checkServiceHealth: (serviceSlug: string) => {
    return api.get(`/services/${serviceSlug}/health`).then(response => response.data);
  },

  batchHealthCheck: (serviceSlugs: string[]) => {
    return api.post('/services/batch-health-check', serviceSlugs).then(response => response.data);
  },
};

// 설정 관리
export const settingsAdminAPI = {
  getSystemSetting: (key: string) => {
    return api.get(`/admin/settings/${key}`).then(response => response.data);
  },
  
  updateSystemSetting: (key: string, value: string) => {
    return api.put(`/admin/settings/${key}`, { setting_value: value }).then(response => response.data);
  },
  
  testEmail: (recipientEmail: string) => {
    return api.post('/admin/settings/test-email', { recipient_email: recipientEmail }).then(response => response.data);
  },
  
  getAllSettings: () => {
    return api.get('/admin/settings').then(response => response.data);
  },
  
  getMainDomain: () => {
    return api.get('/admin/settings/main-domain').then(response => response.data);
  },
  
  updateMainDomain: (domain: string) => {
    return api.put('/admin/settings/main-domain', { setting_value: domain }).then(response => response.data);
  },
  
  getPlans: () => {
    return api.get('/admin/plans').then(response => response.data);
  },
  
  // 이메일 템플릿 API
  getEmailTemplates: () => {
    return api.get('/admin/email-templates').then(response => response.data);
  },
  
  getEmailTemplate: (templateId: string) => {
    return api.get(`/admin/email-templates/${templateId}`).then(response => response.data);
  },
  
  updateEmailTemplate: (templateId: string, content: string) => {
    return api.put(`/admin/email-templates/${templateId}`, { content }).then(response => response.data);
  },
  
  previewEmailTemplate: (templateId: string, sampleData?: Record<string, any>) => {
    return api.post(`/admin/email-templates/${templateId}/preview`, { sample_data: sampleData || {} }).then(response => response.data);
  },
  
  testEmailTemplate: (templateId: string, recipientEmail: string, sampleData?: Record<string, any>) => {
    return api.post(`/admin/email-templates/${templateId}/test`, { 
      recipient_email: recipientEmail,
      sample_data: sampleData || {}
    }).then(response => response.data);
  },
  
  resetAllEmailTemplates: () => {
    return api.delete('/admin/email-templates/reset').then(response => response.data);
  },
};

// 로그 관리
export const logsAdminAPI = {
  getActivityLogs: (params: {
    skip?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
    activity_type?: string;
    activity_category?: string;
    log_level?: string;
    user_id?: number;
    customer_id?: number;
    user_search?: string;
    description_search?: string;
    ip_address_search?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.activity_type) queryParams.append('activity_type', params.activity_type);
    if (params.activity_category) queryParams.append('activity_category', params.activity_category);
    if (params.log_level) queryParams.append('log_level', params.log_level);
    if (params.user_id !== undefined) queryParams.append('user_id', params.user_id.toString());
    if (params.customer_id !== undefined) queryParams.append('customer_id', params.customer_id.toString());
    if (params.user_search) queryParams.append('user_search', params.user_search);
    if (params.description_search) queryParams.append('description_search', params.description_search);
    if (params.ip_address_search) queryParams.append('ip_address_search', params.ip_address_search);
    return api.get(`/admin/logs?${queryParams.toString()}`).then(response => response.data);
  },
  
  getAccessLogs: (customerId?: number, serviceSlug?: string, userId?: number, startDate?: string, endDate?: string, skip = 0, limit = 100) => {
    const params: any = { skip, limit };
    if (customerId) params.customer_id = customerId;
    if (serviceSlug) params.service_slug = serviceSlug;
    if (userId) params.user_id = userId;
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    return api.get(`/metering/access-logs`, { params }).then(response => response.data);
  },
};

// 요금제(가격표) 관리 — 옛 구독 · 크레딧 · 결제 이력 함수는 지웠다 (#234)
export const billingAdminAPI = {
  // 쿼타 플랜 관리 API
  getServiceQuotaPlans: (serviceSlug: string, includeInactive = false) => {
    return api.get(`/billing/services/${serviceSlug}/quota-plans`, {
      params: { include_inactive: includeInactive }
    }).then(response => response.data);
  },

  createQuotaPlan: (serviceSlug: string, planData: any) => {
    return api.post(`/billing/services/${serviceSlug}/quota-plans`, planData).then(response => response.data);
  },

  updateQuotaPlan: (serviceSlug: string, planId: number, planData: any) => {
    return api.put(`/billing/services/${serviceSlug}/quota-plans/${planId}`, planData).then(response => response.data);
  },

  // 서비스가 올린 요금제 승인 · 반려 (#79)
  approveQuotaPlan: (serviceSlug: string, planId: number) => {
    return api.post(`/billing/services/${serviceSlug}/quota-plans/${planId}/approve`).then(response => response.data);
  },

  rejectQuotaPlan: (serviceSlug: string, planId: number, reason: string) => {
    return api.post(`/billing/services/${serviceSlug}/quota-plans/${planId}/reject`, { reason }).then(response => response.data);
  },

  deleteQuotaPlan: (serviceSlug: string, planId: number) => {
    return api.delete(`/billing/services/${serviceSlug}/quota-plans/${planId}`).then(response => response.data);
  },
};

// 통합 adminAPI (하위 호환성을 위해)
export const adminAPI = {
  ...customerAdminAPI,
  ...userAdminAPI,
  ...serviceAdminAPI,
  ...settingsAdminAPI,
  ...logsAdminAPI,
  ...billingAdminAPI,
};

